// Context questions: questions that share a passage (a reading text, a table, a chart).
// In the builder a row's passage is `item.question.passage` ({uuid, name}); in an attempt or a review
// each question carries `passage_uuid` (null for ordinary questions) and the bodies come once in `passages`.

const passageOf = (item) => item?.question?.passage?.uuid || null;

/**
 * Runs of consecutive exam questions of one test with the same passage. Every run shows the whole
 * passage; one passage can form several runs (two tests of an exam, or old data). Indices are positions
 * in `questions` (display order).
 * Returns {runs: [{key, passageUuid, testUuid, start, end}], runByIndex: [run | null per question]}.
 */
export function passageRuns(questions) {
  const list = questions || [];
  const runs = [];
  const runByIndex = new Array(list.length).fill(null);
  let current = null;
  list.forEach((q, i) => {
    const passageUuid = q?.passage_uuid || null;
    const testUuid = q?.test_uuid ?? null;
    if (!passageUuid) {
      current = null;
      return;
    }
    if (!current || current.passageUuid !== passageUuid || current.testUuid !== testUuid) {
      current = { key: `${testUuid}:${passageUuid}:${i}`, passageUuid, testUuid, start: i, end: i };
      runs.push(current);
    }
    current.end = i;
    runByIndex[i] = current;
  });
  return { runs, runByIndex };
}

/**
 * The builder's rows as units: a standalone row, or a block of consecutive rows of one passage.
 * A passage whose rows are not all next to each other gives several blocks, each with `split: true`.
 * {kind: "row", key, item, start, end} | {kind: "passage", key, passage, items, start, end, split}
 * (`start`/`end` are row indices).
 */
export function compositionUnits(items) {
  const units = [];
  const runsOf = new Map();
  (items || []).forEach((item, i) => {
    const uuid = passageOf(item);
    const last = units[units.length - 1];
    if (uuid && last?.kind === "passage" && last.passage.uuid === uuid) {
      last.items.push(item);
      last.end = i;
    } else if (uuid) {
      const n = runsOf.get(uuid) || 0;
      runsOf.set(uuid, n + 1);
      units.push({ kind: "passage", key: `p:${uuid}:${n}`, passage: item.question.passage, items: [item], start: i, end: i, split: false });
    } else {
      units.push({ kind: "row", key: item.uuid, item, start: i, end: i });
    }
  });
  units.forEach((u) => {
    if (u.kind === "passage") u.split = runsOf.get(u.passage.uuid) > 1;
  });
  return units;
}

/**
 * The optimistic copy of the server's move_passage: the passage's rows (in their current order) become
 * one block placed after `k` of the other rows. A block never lands inside another passage's block: it
 * goes past that block in the direction it moves. Returns a new array (the same one if nothing to move).
 */
export function placePassage(items, passageUuid, k) {
  const block = items.filter((it) => passageOf(it) === passageUuid);
  if (!block.length) return items;
  const rest = items.filter((it) => passageOf(it) !== passageUuid);
  const firstIndex = items.indexOf(block[0]);
  let t = Math.max(0, Math.min(k, rest.length));
  const other = t > 0 && t < rest.length ? passageOf(rest[t - 1]) : null;
  if (other && other === passageOf(rest[t])) {
    if (t > firstIndex) {
      while (t < rest.length && passageOf(rest[t]) === other) t += 1;
    } else {
      while (t > 0 && passageOf(rest[t - 1]) === other) t -= 1;
    }
  }
  return [...rest.slice(0, t), ...block, ...rest.slice(t)];
}

/**
 * What dragging unit `from` to unit position `to` means for the rows:
 * {type: "row", itemUuid, toIndex} (one row to a new index), {type: "passage", passageUuid, k}
 * (move_passage: the block goes after `k` rows of other questions), or null when nothing moves.
 */
export function unitMoveTarget(units, from, to) {
  if (from === to || !units?.[from] || !units?.[to]) return null;
  const moved = [...units];
  const [unit] = moved.splice(from, 1);
  moved.splice(to, 0, unit);
  const size = (u) => (u.kind === "row" ? 1 : u.items.length);
  const before = moved.slice(0, moved.indexOf(unit));
  if (unit.kind === "row") {
    return { type: "row", itemUuid: unit.item.uuid, toIndex: before.reduce((n, u) => n + size(u), 0) };
  }
  const uuid = unit.passage.uuid;
  const k = before.reduce((n, u) => n + (u.kind === "passage" && u.passage.uuid === uuid ? 0 : size(u)), 0);
  return { type: "passage", passageUuid: uuid, k };
}
